/**
 * Pure-function decision policy for what the Service Worker displays after
 * attempting to decrypt an encrypted Web Push envelope.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/NSERenderPolicy.swift` exactly.
 *
 * Threat-model rules baked into this policy:
 *
 *   - Decryption failure (decryptedBody === null) ALWAYS yields
 *     `{ tag: "generic" }` with a fixed "New message" body. The SW must
 *     never differentiate "no session" / "tampered ciphertext" /
 *     "unsupported schema" through the user-visible notification — that
 *     would let a passive or active adversary distinguish failure modes
 *     from outside the device.
 *
 *   - Sender name and preview text come ONLY from the LOCAL roster
 *     (`senderTrustedName`, `threadDisplayName`). There is no parameter
 *     for a peer-claimed display string, by design — a hostile peer or
 *     server cannot inject display strings into the notification.
 *     The `from_name` / `display_name` field on the wire payload is
 *     intentionally absent from this function's signature.
 *
 *   - Muted threads still produce a notification (the SW must call
 *     `showNotification` with something), but collapse to generic with an
 *     empty body so the system-level suppression is the visible state.
 *
 *   - The body is hard-truncated to 250 unicode scalars + ellipsis to
 *     bound SW memory and to limit accidental on-screen leakage.
 *
 * Pure-logic only: no `self.registration.showNotification()` calls.
 */

import type { SWPushKaetherPayload } from "./SWPushPayload";

// ---------------------------------------------------------------------------
// Roster types  (local-only; the caller populates these)
// ---------------------------------------------------------------------------

/**
 * Minimal local-roster view the caller must supply. All fields come from
 * trusted local storage — never from the push payload wire format.
 */
export interface LocalRoster {
  /**
   * Look up the trusted display name for a sender from local contacts.
   * Returns `undefined` if the sender is not in the local roster.
   */
  readonly senderName: (fromUserId: string) => string | undefined;

  /**
   * Whether the sender is in the local roster (user has explicitly added
   * them as a contact).
   */
  readonly isSenderInRoster: (fromUserId: string) => boolean;

  /**
   * Look up a thread (group/DM) display name from local roster.
   * Returns `undefined` if not applicable or unknown.
   */
  readonly threadDisplayName: (fromUserId: string) => string | undefined;

  /**
   * Whether the thread is muted by the user.
   */
  readonly isThreadMuted: (fromUserId: string) => boolean;
}

// ---------------------------------------------------------------------------
// Settings stub  (caller supplies)
// ---------------------------------------------------------------------------

export interface SWNotificationSettings {
  /** If true, show message preview in notifications. Defaults to true. */
  readonly showPreview?: boolean;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/**
 * The visible notification content. Used by the SW `push` handler to call
 * `self.registration.showNotification(title, { body, ... })`.
 */
export interface SWNotificationDisplay {
  readonly title: string;
  readonly body: string;
  /** Used as `data.threadId` on the notification for reply-from-notification routing. */
  readonly threadId: string;
  /** Optional icon URL (caller resolves from local cache). */
  readonly icon?: string;
}

/**
 * Mirrors `NSERenderOutcome`. The `generic` variant has NO associated
 * values — that invariant ensures no peer/server input can leak through
 * the generic path.
 */
export type SWRenderOutcome =
  | { readonly tag: "generic" }
  | { readonly tag: "rendered"; readonly display: SWNotificationDisplay };

// ---------------------------------------------------------------------------
// Policy constants  (all mirrored from NSERenderPolicy)
// ---------------------------------------------------------------------------

/** Body length cap (unicode scalars) before truncation + ellipsis. */
const MAX_BODY_SCALARS = 250;

/** Fixed notification category for all kaether push notifications. */
export const KAETHER_CATEGORY = "kaetherMessage";

/** Fixed title for the leak-free generic fallback (decrypt failure / muted). */
export const GENERIC_TITLE = "New message";

/**
 * Fixed title used when we have decrypted text but cannot resolve a trusted
 * sender name from the local roster.
 */
export const UNRESOLVED_SENDER_TITLE = "Message";

// ---------------------------------------------------------------------------
// Pure-function render
// ---------------------------------------------------------------------------

/**
 * Decides the visible notification for a freshly-parsed push payload.
 *
 * IMPORTANT: this function has no `peerClaimedName` / `fromName` parameter
 * by design. Sender name comes only from `localRoster.senderName()` (local
 * contacts). Thread name comes only from `localRoster.threadDisplayName()`
 * (local contacts). A hostile peer or server cannot inject display strings
 * into the notification through this function.
 *
 * @param payload        Validated kaether sub-object from the push event.
 * @param decryptResult  Decrypted plaintext body, or `null` if decryption
 *                       failed for ANY reason. The caller MUST pass `null`
 *                       on any failure — no error string is accepted.
 * @param localRoster    Trusted local-only contact/thread lookup.
 * @param settings       Optional notification display settings.
 */
export function render(
  payload: SWPushKaetherPayload,
  decryptResult: string | null,
  localRoster: LocalRoster,
  settings?: SWNotificationSettings,
): SWRenderOutcome {
  // Decrypt failure: opaque generic. No leakage of why.
  if (decryptResult === null) {
    return { tag: "generic" };
  }

  // Muted thread: collapse to generic (system-level suppression is the
  // visible state; we do not leak preview from a thread the user muted).
  if (localRoster.isThreadMuted(payload.fromUserId)) {
    return { tag: "generic" };
  }

  // Resolve title from LOCAL roster ONLY.
  let title: string;
  const threadName = localRoster.threadDisplayName(payload.fromUserId);
  if (threadName !== undefined && threadName.length > 0) {
    title = threadName;
  } else if (
    localRoster.isSenderInRoster(payload.fromUserId)
  ) {
    const trusted = localRoster.senderName(payload.fromUserId);
    title = (trusted !== undefined && trusted.length > 0) ? trusted : UNRESOLVED_SENDER_TITLE;
  } else {
    title = UNRESOLVED_SENDER_TITLE;
  }

  // Truncate body at 250 unicode scalars (use spread so surrogate pairs /
  // emoji sequences are counted as scalars, not UTF-16 code units).
  const body = truncateScalars(decryptResult, MAX_BODY_SCALARS);

  return {
    tag: "rendered",
    display: {
      title,
      body,
      threadId: payload.fromUserId,
    },
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Truncate `text` to at most `max` unicode scalars. If truncation occurs,
 * appends a single "…" character (U+2026 HORIZONTAL ELLIPSIS).
 *
 * Uses `[...str]` spread (which splits on Unicode scalar values, not UTF-16
 * code units) so emoji and other multi-code-unit characters are counted
 * correctly. Mirrors iOS `String.prefix(max)` + ellipsis.
 */
function truncateScalars(text: string, max: number): string {
  const scalars = [...text];
  if (scalars.length <= max) {
    return text;
  }
  return scalars.slice(0, max).join("") + "…";
}
